/**
 * BeezFy Theme JavaScript v1.0.0
 * Complete functionality for modern e-commerce
 */

(function() {
  'use strict';
  const BEEZFY_THEME_DEBUG = false;
  const themeDebug = (...args) => { if (BEEZFY_THEME_DEBUG) console.log(...args); };


  // Theme namespace
  window.BeezFy = window.BeezFy || {};

  /**
   * Product Form with Variant Selection
   */
  class ProductForm {
    constructor(form) {
      // Validate that form is actually an HTMLFormElement
      if (!(form instanceof HTMLFormElement)) {
        console.error('ProductForm: Invalid form element provided', form);
        return;
      }

      this.form = form;
      this.addToCartButton = form.querySelector('[name="add"]');
      this.variantIdInput = form.querySelector('[name="id"]');

      if (this.form) {
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      }
    }

    onSubmitHandler(evt) {
      evt.preventDefault();

      // Additional validation
      if (!this.form || !(this.form instanceof HTMLFormElement)) {
        console.error('ProductForm: Form is not valid during submit');
        return;
      }

      if (this.addToCartButton && this.addToCartButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();
      this.addToCartButton.setAttribute('aria-disabled', true);
      this.addToCartButton.classList.add('loading');

      // Check if product has variants and should open wizard
      if (this.shouldOpenWizard()) {
        themeDebug('Product has variants, opening wizard instead of direct add to cart');
        this.openVariantWizard();
        return;
      }

      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        }
      };

      // Double-check form validity before creating FormData
      if (!this.form || !(this.form instanceof HTMLFormElement)) {
        console.error('ProductForm: Cannot create FormData - form is invalid');
        this.handleErrorMessage('Errore interno del form');
        return;
      }

      const formData = new FormData(this.form);

      themeDebug('Form data being sent:');
      for (let [key, value] of formData.entries()) {
        themeDebug(`${key}: ${value}`);
      }

      // Check if variant ID is present and valid
      const variantId = formData.get('id');
      themeDebug('Variant ID from form:', variantId);

      if (!variantId || variantId === 'undefined' || variantId === 'null') {
        console.error('Invalid variant ID in form data');
        this.handleErrorMessage('ID variante non valido');
        this.addToCartButton.classList.remove('loading');
        this.addToCartButton.removeAttribute('aria-disabled');
        return;
      }

      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => {
          themeDebug('Cart add response status:', response.status);
          themeDebug('Cart add response headers:', response.headers);

          if (!response.ok) {
            console.error('Cart add failed with status:', response.status);

            // Handle specific error codes
            if (response.status === 422) {
              console.error('422 Error - checking response details...');
              return response.text().then(text => {
                console.error('422 Response text:', text);
                try {
                  const jsonResponse = JSON.parse(text);
                  console.error('422 JSON response:', jsonResponse);
                  this.handleErrorMessage(jsonResponse.description || 'Errore nell\'aggiunta al carrello (422)');
                } catch (e) {
                  console.error('Could not parse 422 response as JSON');
                  this.handleErrorMessage('Errore nell\'aggiunta al carrello. Riprova tra qualche istante.');
                }
                return;
              });
            } else if (response.status === 429) {
              this.handleErrorMessage('Troppe richieste. Riprova tra qualche istante.');
              return;
            } else if (response.status >= 500) {
              this.handleErrorMessage('Errore del server. Riprova più tardi.');
              return;
            }
          }

          return response.json();
        })
        .then((response) => {
          if (!response || response.status) {
            // Error already handled above
            return;
          }

          themeDebug('Cart add success:', response);

          // Show success message
          this.showNotification('✅ Prodotto aggiunto al carrello!', 'success');

          // Open cart drawer if available
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            setTimeout(() => cartDrawer.open(), 500);
          }

          this.renderContents(response);
        })
        .catch((e) => {
          console.error('Cart add fetch error:', e);
          this.handleErrorMessage('Errore di connessione. Verifica la tua connessione internet.');
        })
        .finally(() => {
          this.addToCartButton.classList.remove('loading');
          this.addToCartButton.removeAttribute('aria-disabled');
        });
    }

    // Check if product has variants that require wizard
    shouldOpenWizard() {
      try {
        // Only run wizard on product pages
        if (!window.location.pathname.includes('/products/')) {
          themeDebug('Not on product page, skipping wizard check');
          return false;
        }

        // Check if wizard is enabled from theme settings
        let wizardEnabled = true; // Default fallback
        try {
          // Try to get from theme settings if available
          if (window.themeSettings && window.themeSettings.enable_variant_wizard !== undefined) {
            wizardEnabled = window.themeSettings.enable_variant_wizard;
          }
        } catch (e) {
          console.warn('Could not check wizard setting, using default');
        }

        if (!wizardEnabled) {
          themeDebug('Wizard is disabled in theme settings');
          return false;
        }

        // Try multiple ways to detect variants (Shopify Dawn theme structure)
        const variantSelects = document.querySelectorAll('variant-selects');
        const productFormInputs = document.querySelectorAll('.product-form__input');
        const swatchInputs = document.querySelectorAll('.product-form__input--swatch');
        const pillInputs = document.querySelectorAll('.product-form__input--pill');
        const dropdownInputs = document.querySelectorAll('.product-form__input--dropdown');
        const selectElements = document.querySelectorAll('select[name*="options["]');

        themeDebug('Checking for variants:');
        themeDebug('- Variant-selects elements found:', variantSelects.length);
        themeDebug('- Product-form__input elements found:', productFormInputs.length);
        themeDebug('- Swatch inputs found:', swatchInputs.length);
        themeDebug('- Pill inputs found:', pillInputs.length);
        themeDebug('- Dropdown inputs found:', dropdownInputs.length);
        themeDebug('- Select elements found:', selectElements.length);

        // Check for variant picker elements
        if (variantSelects.length > 0) {
          themeDebug('Variant-selects found, product has variants');
          return true;
        }

        // Check for multiple form inputs (multiple options)
        if (productFormInputs.length > 1) {
          themeDebug('Multiple product form inputs found, product has variants');
          return true;
        }

        // Check for any variant-related inputs
        if (swatchInputs.length > 0 || pillInputs.length > 0 || dropdownInputs.length > 0) {
          themeDebug('Variant input elements found, product has variants');
          return true;
        }

        // Check for option select elements
        if (selectElements.length > 0) {
          themeDebug('Option select elements found, product has variants');
          return true;
        }

        // Check for data attributes on the form
        const productForm = document.querySelector('form[action*="/cart/add"]');
        if (productForm) {
          const formData = new FormData(productForm);
          const variantId = formData.get('id');
          themeDebug('Variant ID from form:', variantId);

          // If there's no variant ID or it's not a number, might need wizard
          if (!variantId || isNaN(parseInt(variantId))) {
            themeDebug('No valid variant ID found, should open wizard');
            return true;
          }
        }

        themeDebug('No variants detected');
        return false;
      } catch (error) {
        console.error('Error checking wizard requirement:', error);
        return false;
      }
    }

    // Open variant selection wizard
    openVariantWizard() {
      themeDebug('Opening variant wizard...');

      // Reset button state
      this.addToCartButton.classList.remove('loading');
      this.addToCartButton.removeAttribute('aria-disabled');

      // Get product data from the page
      const productData = this.getProductDataForWizard();
      if (!productData) {
        console.error('Could not get product data for wizard - falling back to manual selection');
        this.fallbackToManualSelection();
        return;
      }

      // Validate product data
      if (!productData.id || !productData.title || !Array.isArray(productData.variants) || !Array.isArray(productData.options)) {
        console.error('Invalid product data structure - falling back to manual selection');
        this.fallbackToManualSelection();
        return;
      }

      try {
        // Create variant wizard element
        const wizardElement = document.createElement('variant-wizard');

        // Double-check JSON serialization
        let productJson;
        try {
          themeDebug('About to stringify productData:', productData);
          productJson = JSON.stringify(productData);
          themeDebug('JSON.stringify result:', productJson);
          themeDebug('JSON.stringify result type:', typeof productJson);
          themeDebug('JSON.stringify result length:', productJson.length);

          // Check if the result is actually valid
          if (productJson === 'undefined' || productJson === undefined || productJson === null || productJson === '') {
            throw new Error('JSON.stringify produced invalid result: ' + productJson);
          }

          // Validate by parsing back
          const testParse = JSON.parse(productJson);
          themeDebug('JSON validation successful, wizard data ready');
        } catch (jsonError) {
          console.error('JSON serialization failed:', jsonError);
          console.error('Original productData:', productData);
          console.error('JSON.stringify result:', productJson);
          this.fallbackToManualSelection();
          return;
        }

        themeDebug('Assigning productJson to dataset:', productJson.substring(0, 50) + '...');
        wizardElement.dataset.productJson = productJson;
        themeDebug('Dataset assigned, checking:', wizardElement.dataset.productJson);

        // Add event listener for variant selection
        wizardElement.addEventListener('variant-selected', (event) => {
          const variantId = event.detail.variantId;
          themeDebug('Variant selected from wizard:', variantId);
          this.addVariantToCart(variantId);
        });

        // Add to DOM to trigger the wizard
        document.body.appendChild(wizardElement);

        themeDebug('Variant wizard modal opened successfully with valid data');
      } catch (error) {
        console.error('Error creating wizard element:', error);
        console.error('Product data that failed:', productData);
        this.fallbackToManualSelection();
      }
    }

    // Get product data for the wizard
    getProductDataForWizard() {
      try {
        themeDebug('Getting product data for wizard...');

        // Get product ID
        let productId = this.form.querySelector('[name="product-id"]')?.value ||
                       this.form.querySelector('[name="id"]')?.value;

        // Try to get from URL if not found in form
        if (!productId) {
          const urlMatch = window.location.pathname.match(/\/products\/([^\/]+)/);
          if (urlMatch) {
            productId = urlMatch[1];
          }
        }

        if (!productId) {
          console.error('Could not find product ID');
          return null;
        }

        themeDebug('Found product ID:', productId);

        // Get product title - try multiple selectors to find the best one
        let productTitle = '';

        // Try different selectors, preferring the most specific ones
        const titleSelectors = [
          '.product__title',
          'h1.product-title',
          'h1',
          '.product-single__title',
          '[data-product-title]'
        ];

        for (const selector of titleSelectors) {
          const element = document.querySelector(selector);
          if (element) {
            const text = element.textContent?.trim();
            if (text && text.length > 3 && text.length < 100) {
              productTitle = text;
              themeDebug('Found title with selector:', selector, 'text:', text);
              break;
            }
          }
        }

        if (!productTitle) {
          productTitle = 'Product';
        }

        themeDebug('Raw product title:', productTitle);

        // Clean up the title - remove extra whitespace and normalize
        productTitle = productTitle.replace(/\s+/g, ' ').trim();
        // Remove invisible characters and weird Unicode
        productTitle = productTitle.replace(/[\u0000-\u001F\u007F-\u009F\u2000-\u200F\u2028-\u202F\u205F-\u206F]/g, '');
        // Keep only printable characters
        productTitle = productTitle.replace(/[^\x20-\x7E]/g, '');

        themeDebug('Cleaned product title:', productTitle);

        if (!productTitle || productTitle === 'Product' || productTitle.length < 3) {
          console.error('Could not find valid product title');
          return null;
        }

        themeDebug('Found product title:', productTitle);

        // Get variant data from JSON scripts - prioritize specific data scripts
        let variants = [];
        let options = [];
        let selectedVariant = null;

        themeDebug('Looking for variant scripts...');

        // Log all JSON scripts found
        const allJsonScripts = document.querySelectorAll('script[type="application/json"], script[data-variants], script[data-product], script[data-options], script[data-selected-variant]');
        themeDebug('All JSON scripts found:', allJsonScripts.length);
        allJsonScripts.forEach((script, index) => {
          themeDebug(`Script ${index}:`, script.id || 'no-id', script.dataset, 'length:', script.textContent?.length || 0);
        });

        // First, try to get data from specific dataset scripts
        const variantsScript = document.querySelector('script[data-variants]');
        const optionsScript = document.querySelector('script[data-options]');
        const selectedVariantScript = document.querySelector('script[data-selected-variant]');
        const productJsonScript = document.querySelector('script[id*="ProductJson"]') || document.querySelector('script[id*="product"][id*="json"]');

        themeDebug('Found specific scripts:');
        themeDebug('- Variants script:', !!variantsScript);
        themeDebug('- Options script:', !!optionsScript);
        themeDebug('- Selected variant script:', !!selectedVariantScript);
        themeDebug('- Product JSON script:', !!productJsonScript);

        // Try to extract from specific scripts first
        if (variantsScript) {
          try {
            const variantsData = JSON.parse(variantsScript.textContent?.trim() || '[]');
            if (Array.isArray(variantsData) && variantsData.length > 0) {
              variants = variantsData;
              themeDebug('Successfully extracted variants from data-variants script:', variants.length, 'variants');
            }
          } catch (e) {
            console.error('Error parsing variants from data-variants script:', e);
          }
        }

        if (optionsScript) {
          try {
            const scriptContent = optionsScript.textContent?.trim();
            themeDebug('Options script content:', scriptContent?.substring(0, 100) + '...');
            const optionsData = JSON.parse(scriptContent || '[]');
            themeDebug('Parsed options data:', optionsData, 'type:', typeof optionsData, 'isArray:', Array.isArray(optionsData));

            if (Array.isArray(optionsData) && optionsData.length > 0) {
              options = optionsData;
              themeDebug('Successfully extracted options from data-options script:', options.length, 'options');

              options.forEach((opt, index) => {
                themeDebug(`Option ${index}:`, opt, 'type:', typeof opt, 'length:', opt?.length, 'trimmed:', opt?.trim?.());
              });
            } else {
              themeDebug('Options data is not a valid array or is empty:', optionsData);
            }
          } catch (e) {
            console.error('Error parsing options from data-options script:', e);
            console.error('Script content that failed:', optionsScript.textContent?.substring(0, 200));
          }
        }

        if (selectedVariantScript) {
          try {
            const selectedVariantContent = selectedVariantScript.textContent?.trim();
            themeDebug('Selected variant script content:', selectedVariantContent?.substring(0, 100) + '...');

            if (selectedVariantContent && selectedVariantContent !== 'null' && selectedVariantContent !== 'undefined') {
              selectedVariant = JSON.parse(selectedVariantContent);
              themeDebug('Parsed selected variant:', selectedVariant, 'type:', typeof selectedVariant);
            } else {
              themeDebug('Selected variant script is empty or null');
              selectedVariant = null;
            }
          } catch (e) {
            console.error('Error parsing selected variant from data-selected-variant script:', e);
            selectedVariant = null;
          }
        }

        // If we didn't get variants from specific script, try ProductJson
        if (variants.length === 0 && productJsonScript) {
          themeDebug('Trying to extract variants from ProductJson script...');
          try {
            const productData = JSON.parse(productJsonScript.textContent?.trim() || '{}');
            themeDebug('ProductJson keys:', Object.keys(productData));

            if (productData.variants && Array.isArray(productData.variants)) {
              variants = productData.variants;
              themeDebug('Extracted variants from ProductJson:', variants.length, 'variants');

              if (productData.options && Array.isArray(productData.options)) {
                options = productData.options;
                themeDebug('Extracted options from ProductJson:', options.length, 'options');
              }
            }
          } catch (e) {
            console.error('Error parsing ProductJson:', e);
          }
        }

        // Final fallback - use any large JSON script
        if (variants.length === 0) {
          const largeScripts = Array.from(allJsonScripts).filter(script => {
            const content = script.textContent?.trim();
            return content && content.length > 1000; // Look for scripts with substantial content
          }).sort((a, b) => (b.textContent?.length || 0) - (a.textContent?.length || 0)); // Sort by length descending

          themeDebug('Large scripts found:', largeScripts.length);

          for (const script of largeScripts) {
            try {
              const data = JSON.parse(script.textContent?.trim() || '{}');
              themeDebug('Trying large script:', script.id || 'no-id', 'keys:', Object.keys(data));

              if (data.variants && Array.isArray(data.variants) && data.variants.length > 0) {
                variants = data.variants;
                if (data.options) options = data.options;
                themeDebug('Found variants in large script:', script.id || 'no-id');
                break;
              }
            } catch (e) {
              console.error('Error parsing large script:', script.id || 'no-id', e);
            }
          }
        }

        themeDebug('Final variants extracted:', variants?.length || 0, 'variants');
        themeDebug('Final options extracted:', options?.length || 0, 'options');

        if (!Array.isArray(variants) || variants.length === 0) {
          console.error('No variants found or invalid format');
          return null;
        }

        // If we don't have options from scripts, try to extract them from the page
        if (options.length === 0) {
          themeDebug('No options from scripts, trying page extraction...');
          options = this.extractOptionsFromPage();
          themeDebug('Extracted options from page:', options);
        } else {
          themeDebug('Using options from scripts:', options);
        }

        if (!Array.isArray(options) || options.length === 0) {
          console.error('No options found');
          return null;
        }

        // Clean and validate all data before creating productData
        themeDebug('Cleaning data - options before filter:', options);
        const cleanVariants = variants.filter(v => v && typeof v === 'object' && v.id);

        // Handle options - they can be strings or objects with name property
        const normalizeOptionName = (name) => typeof name === 'string' ? name.trim() : '';

        const buildOptionRecord = (name, index) => {
          const optionIndex = typeof index === 'number' ? index : 0;
          const values = Array.from(new Set(
            cleanVariants
              .map(variant => {
                if (Array.isArray(variant.options) && variant.options.length > optionIndex) {
                  return variant.options[optionIndex];
                }
                const key = `option${optionIndex + 1}`;
                if (variant && typeof variant === 'object' && variant[key] !== undefined) {
                  return variant[key];
                }
                return null;
              })
              .filter(Boolean)
              .map(value => typeof value === 'string' ? value.trim() : String(value))
          ));

          return {
            name,
            position: optionIndex + 1,
            values
          };
        };

        const optionRecords = [];
        const seenOptionNames = new Set();

        options.forEach((opt, index) => {
          let optionName = '';
          if (typeof opt === 'string') {
            optionName = normalizeOptionName(opt);
          } else if (opt && typeof opt === 'object') {
            optionName = normalizeOptionName(opt.name || opt.label || opt.title);
          }

          if (!optionName || seenOptionNames.has(optionName)) {
            return;
          }

          seenOptionNames.add(optionName);

          let record;
          if (opt && typeof opt === 'object' && Array.isArray(opt.values) && opt.values.length) {
            record = {
              name: optionName,
              position: typeof opt.position === 'number' ? opt.position : index + 1,
              values: opt.values
                .filter(Boolean)
                .map(value => typeof value === 'string' ? value.trim() : String(value))
            };
          }

          if (!record || !Array.isArray(record.values) || record.values.length === 0) {
            record = buildOptionRecord(optionName, index);
          }

          if (record.values.length === 0) {
            themeDebug('Option has no values after processing, skipping:', optionName);
            return;
          }

          optionRecords.push(record);
        });

        const cleanOptions = optionRecords;

        const cleanSelectedVariant = (selectedVariant && typeof selectedVariant === 'object' && selectedVariant.id) ? selectedVariant : null;

        themeDebug('After cleaning - cleanOptions:', cleanOptions, 'length:', cleanOptions.length);

        const productData = {
          id: String(productId).trim(),
          title: String(productTitle).trim(),
          variants: cleanVariants,
          options: cleanOptions,
          selectedVariant: cleanSelectedVariant
        };

        themeDebug('Product data for wizard:', {
          id: productData.id,
          title: productData.title,
          variantsCount: productData.variants?.length || 0,
          optionsCount: productData.options?.length || 0,
          hasSelectedVariant: !!productData.selectedVariant
        });

        // Final validation - ensure data is serializable
        themeDebug('Final productData before serialization:', productData);
        try {
          const testJson = JSON.stringify(productData);
          themeDebug('JSON string length:', testJson.length);
          themeDebug('JSON preview:', testJson.substring(0, 200) + '...');

          const parsedBack = JSON.parse(testJson);
          themeDebug('Parsed back successfully, all fields present');

          // Additional validation of individual fields
          if (typeof parsedBack.id !== 'string' || !parsedBack.id) {
            throw new Error('Invalid id field');
          }
          if (typeof parsedBack.title !== 'string' || !parsedBack.title) {
            throw new Error('Invalid title field');
          }
          if (!Array.isArray(parsedBack.variants) || parsedBack.variants.length === 0) {
            throw new Error('Invalid variants field');
          }
          if (!Array.isArray(parsedBack.options) || parsedBack.options.length === 0) {
            throw new Error('Invalid options field');
          }

          themeDebug('All fields validated successfully');
        } catch (error) {
          console.error('Product data validation failed:', error);
          console.error('Problematic data:', productData);
          console.error('Generated JSON:', JSON.stringify(productData));
          return null;
        }

        return productData;

      } catch (error) {
        console.error('Error getting product data for wizard:', error);
        return null;
      }
    }

    // Extract options from the page (fallback when JSON scripts don't provide them)
    extractOptionsFromPage() {
      const options = [];
      themeDebug('Extracting options from page as fallback...');

      // Try to get from variant selectors - multiple selectors
      const optionSelectors = [
        '.product-form__input legend',
        'fieldset legend',
        '.variant-selects legend',
        'variant-selects fieldset legend',
        '[data-option-name]'
      ];

      for (const selector of optionSelectors) {
        const elements = document.querySelectorAll(selector);
        themeDebug(`Checking selector "${selector}":`, elements.length, 'elements');

        elements.forEach(element => {
          let optionName = '';

          // Try different ways to get the option name
          if (element.hasAttribute('data-option-name')) {
            optionName = element.getAttribute('data-option-name');
          } else if (element.textContent) {
            optionName = element.textContent.trim().replace(':', '').trim();
          }

          if (optionName && !options.includes(optionName)) {
            options.push(optionName);
            themeDebug('Found option from page:', optionName);
          }
        });

        if (options.length > 0) break; // Stop if we found options
      }

      // Fallback: try to get from form inputs
      if (options.length === 0) {
        themeDebug('No options found from legends, trying form inputs...');
        const inputs = document.querySelectorAll('input[name*="options["], select[name*="options["]');
        const optionNames = new Set();

        inputs.forEach(input => {
          const match = input.name.match(/options\[([^\]]+)\]/);
          if (match) {
            const optionName = match[1];
            optionNames.add(optionName);
            themeDebug('Found option from input:', optionName);
          }
        });

        options.push(...Array.from(optionNames));
      }

      // Final fallback: try to get from ProductJson script
      if (options.length === 0) {
        themeDebug('No options found, trying from ProductJson...');
        try {
          const productJsonScript = document.querySelector('script[id*="ProductJson"]');
          if (productJsonScript) {
            const productData = JSON.parse(productJsonScript.textContent);
            if (productData.options && Array.isArray(productData.options)) {
              options.push(...productData.options);
              themeDebug('Found options from ProductJson:', options);
            }
          }
        } catch (e) {
          console.error('Error extracting options from ProductJson:', e);
        }
      }

      themeDebug('Final extracted options from page:', options);
      return options;
    }

    // Fallback to manual variant selection
    fallbackToManualSelection() {
      themeDebug('Falling back to manual variant selection...');

      // Reset button state
      this.addToCartButton.classList.remove('loading');
      this.addToCartButton.removeAttribute('aria-disabled');

      // Find variant selectors
      const variantSelectors = document.querySelectorAll('variant-selects, .product-form__input');

      if (variantSelectors.length > 0) {
        // Scroll to first variant selector
        const firstSelector = variantSelectors[0];
        firstSelector.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

        // Highlight variant selectors briefly
        variantSelectors.forEach(selector => {
          selector.style.transition = 'all 0.3s ease';
          selector.style.border = '2px solid #ff6b6b';
          selector.style.borderRadius = '8px';
          selector.style.background = 'rgba(255, 107, 107, 0.05)';

          setTimeout(() => {
            selector.style.border = '';
            selector.style.background = '';
          }, 3000);
        });

        // Show message
        this.showNotification(
          'Seleziona le opzioni del prodotto qui sopra, poi clicca nuovamente "Aggiungi al carrello"',
          'info'
        );

        themeDebug('Manual selection fallback activated');
      } else {
        // No variant selectors found, just show error
        this.handleErrorMessage('Seleziona le opzioni del prodotto prima di aggiungere al carrello');
      }
    }

    // Add selected variant to cart
    addVariantToCart(variantId) {
      themeDebug('Adding variant to cart:', variantId);

      // Set the variant ID in the form
      const variantInput = this.form.querySelector('[name="id"]');
      if (variantInput) {
        variantInput.value = variantId;
      }

      // Submit the form
      this.form.submit();
    }


    showNotification(message, type = 'info') {
      // Remove existing notifications
      const existing = document.querySelectorAll('.beezfy-notification');
      existing.forEach(n => n.remove());

      const notification = document.createElement('div');
      notification.className = `beezfy-notification beezfy-notification--${type}`;
      notification.innerHTML = `
        <div class="beezfy-notification__content">
          <span class="beezfy-notification__icon">${type === 'success' ? '✓' : 'ℹ'}</span>
          <span class="beezfy-notification__message">${message}</span>
        </div>
      `;

      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: ${type === 'success' ? '#10b981' : '#3b82f6'};
        color: white;
        padding: 12px 16px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 10000;
        font-size: 14px;
        font-weight: 500;
        max-width: 300px;
        word-wrap: break-word;
        opacity: 0;
        transform: translateY(-20px);
        transition: all 0.3s ease;
      `;

      document.body.appendChild(notification);

      // Animate in
      setTimeout(() => {
        notification.style.opacity = '1';
        notification.style.transform = 'translateY(0)';
      }, 10);

      // Auto remove after 3 seconds
      setTimeout(() => {
        notification.style.opacity = '0';
        notification.style.transform = 'translateY(-20px)';
        setTimeout(() => notification.remove(), 300);
      }, 3000);
    }

    handleErrorMessage(errorMessage = false) {
      const errorMessageWrapper = this.form.querySelector('.product-form__error-message-wrapper');
      if (!errorMessageWrapper) return;

      const errorMessageElement = errorMessageWrapper.querySelector('.product-form__error-message');

      errorMessageWrapper.toggleAttribute('hidden', !errorMessageElement);

      if (errorMessageElement) {
        errorMessageElement.textContent = errorMessage;
      }
    }

    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        const elementToReplace = document.getElementById(section.id);
        if (elementToReplace && parsedState.sections && parsedState.sections[section.id]) {
          elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }
      }));

      if (window.CartNotification) window.CartNotification.setActiveElement(document.activeElement);
      if (window.CartNotification) window.CartNotification.renderContents(parsedState);
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        },
        {
          id: 'cart-live-region-text',
          section: 'cart-live-region-text',
          selector: '.shopify-section'
        },
        {
          id: 'cart-notification-product',
          section: 'cart-notification-product',
          selector: '.shopify-section'
        }
      ];
    }

    getSectionInnerHTML(html, selector) {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
  }

  /**
   * Variant Selection Logic
   */
  class VariantSelects {
    constructor(select) {
      this.select = select;
      // Trova la sezione del prodotto dal form più vicino
      this.productForm = select.closest('form');
      this.section = this.productForm ? this.productForm.querySelector('[name="section-id"]')?.value || 'main' : 'main';
      this.addEventListener();
    }

    addEventListener() {
      document.addEventListener('change', this.onVariantChange.bind(this));
    }

    onVariantChange(event) {
      const target = event.target;
      if (!target || !target.matches('.product-form__input input[type="radio"]')) return;

      this.updateOptions();
      this.updateMasterId();
      this.toggleAddButton(true, '', false);
      this.updatePickupAvailability();
      this.updateVariantStatuses();

      if (!target.closest('.product-form__input').hasAttribute('data-product-url')) this.setUnavailable();
    }

    updateOptions() {
      if (!this.select) return;
      const fieldsets = Array.from(this.select.querySelectorAll('.js.product-form__input'));
      this.options = fieldsets.map((fieldset) => {
        return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
      });
    }

    updateMasterId() {
      const variantData = this.getVariantData();
      if (!Array.isArray(variantData)) {
        console.error('VariantSelects: variantData is not an array');
        return;
      }

      this.currentVariant = variantData.find((variant) => {
        return !variant.options.map((option, index) => {
          return this.options[index] === option;
        }).includes(false);
      });
    }

    updatePickupAvailability() {
      const pickUpAvailability = document.querySelector('pickup-availability');
      if (!pickUpAvailability) return;

      if (this.currentVariant && this.currentVariant.available) {
        pickUpAvailability.fetchAvailability(this.currentVariant.id);
      } else {
        pickUpAvailability.removeAttribute('available');
        pickUpAvailability.innerHTML = '';
      }
    }

    updateVariantStatuses() {
      const variantData = this.getVariantData();
      if (!Array.isArray(variantData)) {
        console.error('VariantSelects: variantData is not an array in updateVariantStatuses');
        return;
      }
      const selectedOptionOneVariants = variantData.filter(variant => this.select.querySelector(':checked').value === variant.option1);
      const inputWrappers = [...this.select.querySelectorAll('.product-form__input')];
      inputWrappers.forEach((option, index) => {
        if (index === 0) return;
        const optionInputs = [...option.querySelectorAll('input[type="radio"], option')]
        const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
        const availableOptionInputsValue = selectedOptionOneVariants.filter(variant => variant.available && variant[`option${index}`] === previousOptionSelected).map(variantOption => variantOption[`option${index + 1}`]);
        this.setInputAvailability(optionInputs, availableOptionInputsValue)
      });
    }

    setInputAvailability(listOfOptions, listOfAvailableOptions) {
      listOfOptions.forEach(input => {
        if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
          input.innerText = input.getAttribute('value');
        } else {
          input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', input.getAttribute('value'));
        }
      });
    }

    toggleAddButton(disable = true, text, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.section}`);
      if (!productForm) {
        console.warn('Product form not found for section:', this.section);
        return;
      }
      const addButton = productForm.querySelector('[name="add"]');
      const addButtonText = productForm.querySelector('[name="add"] > span');
      if (!addButton) {
        console.warn('Add button not found in product form');
        return;
      }

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        if (window.variantStrings && window.variantStrings.addToCart) {
          addButtonText.textContent = window.variantStrings.addToCart;
        }
      }
    }

    setUnavailable() {
      const button = document.getElementById(`product-form-${this.section}`);
      if (!button) {
        console.warn('Product form not found for section:', this.section);
        return;
      }
      const addButton = button.querySelector('[name="add"]');
      const addButtonText = button.querySelector('[name="add"] > span');
      const price = document.getElementById(`price-${this.section}`);

      if (!addButton) {
        console.warn('Add button not found in product form');
        return;
      }

      if (window.variantStrings && window.variantStrings.unavailable) {
        addButtonText.textContent = window.variantStrings.unavailable;
      }
      if (price) price.classList.add('visibility-hidden');
    }

    getVariantData() {
      if (!this.select) {
        console.error('VariantSelects: this.select is not defined');
        return [];
      }

      if (this.variantData) {
        return this.variantData;
      }

      const jsonElement = this.select.querySelector('[type="application/json"]');
      if (!jsonElement) {
        console.error('VariantSelects: Could not find variant data JSON element');
        return [];
      }

      try {
        this.variantData = JSON.parse(jsonElement.textContent);
        return this.variantData;
      } catch (error) {
        console.error('VariantSelects: Error parsing variant data JSON:', error);
        return [];
      }
    }
  }

  /**
   * Cart Functionality
   */
  class CartNotification extends HTMLElement {
    constructor() {
      super();

      this.notification = document.getElementById('cart-notification');
      this.header = document.querySelector('sticky-header');
      this.onBodyClick = this.handleBodyClick.bind(this);
      
      this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      this.select.querySelectorAll('button[type="button"]').forEach((closeButton) =>
        closeButton.addEventListener('click', this.close.bind(this))
      );
    }

    open() {
      this.notification.classList.add('animate', 'active');
      
      this.notification.addEventListener('transitionend', () => {
        this.notification.focus();
        trapFocus(this.notification);
      }, { once: true });

      document.body.addEventListener('click', this.onBodyClick);
    }

    close() {
      this.notification.classList.remove('active');
      document.body.removeEventListener('click', this.onBodyClick);

      removeTrapFocus(this.activeElement);
    }

    renderContents(parsedState) {
      this.cartItemKey = parsedState.key;
      this.getSectionsToRender().forEach((section => {
        document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      }));

      if (this.header) this.header.reveal();
      this.open();
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-notification-product',
          section: 'cart-notification-product',
          selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
        },
        {
          id: 'cart-notification-button',
          section: 'cart-notification-button',
          selector: '[data-cart-notification-button]',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }

    handleBodyClick(evt) {
      const target = evt.target;
      if (target !== this.notification && !target.closest('cart-notification')) {
        const disclosure = target.closest('details-disclosure, header-menu');
        this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
        this.close();
      }
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  /**
   * Sticky Header
   */
  class StickyHeader extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.header = document.getElementById('shopify-section-header');
      this.headerBounds = {};
      this.currentScrollTop = 0;
      this.preventReveal = false;

      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => this.preventReveal = true;

      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, false);

      this.createObserver();
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
    }

    createObserver() {
      let observer = new IntersectionObserver((entries, observer) => {
        this.headerBounds = entries[0].intersectionRect;
        observer.unobserve(this.header);
      });

      observer.observe(this.header);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (this.preventReveal) {
          this.isScrollingDown = true;
          this.preventReveal = false;
        }
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (!this.preventReveal) {
          this.header.classList.add('shopify-section-header-sticky');
          this.isScrollingDown = false;
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.header.classList.remove('scrolled-past-header', 'shopify-section-header-sticky');
        this.isScrollingDown = false;
      }

      this.currentScrollTop = scrollTop;
    }

    reveal() {
      if (!this.header.classList.contains('shopify-section-header-sticky')) return;
      this.header.classList.add('shopify-section-header-revealed');
      this.header.addEventListener('transitionend', () => {
        this.header.classList.remove('shopify-section-header-revealed');
      }, { once: true });
    }
  }

  /**
   * Initialize everything
   */
  function init() {
    // Initialize product forms - look for actual form elements
    document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
      if (form instanceof HTMLFormElement) {
        new ProductForm(form);
      }
    });

    // Also try common form selectors for product pages
    const productFormSelectors = [
      'form.product-form',
      'form[action="/cart/add"]',
      '#product-form',
      '.product-form form'
    ];

    productFormSelectors.forEach(selector => {
      const form = document.querySelector(selector);
      if (form && form instanceof HTMLFormElement && !form.beezFyProductForm) {
        themeDebug('Found product form with selector:', selector);
        new ProductForm(form);
      }
    });

    // Initialize variant selects
    document.querySelectorAll('variant-selects').forEach(select => {
      new VariantSelects(select);
    });

    // Register custom elements (cart-notification is defined in cart-notification.js)

    if (customElements.get('sticky-header') === undefined) {
      customElements.define('sticky-header', StickyHeader);
    }

    // Initialize card quick add wizard buttons
    (function initCardQuickAdd() {
      document.querySelectorAll('.card-quick-add--wizard').forEach((button) => {
        if (button.dataset.wizardInit) return;
        button.dataset.wizardInit = 'true';

        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const card = button.closest('.card');
          if (!card) return;

          const variantsJson = card.querySelector('script[data-variants]');
          const optionsJson = card.querySelector('script[data-options]');
          if (!variantsJson || !optionsJson) return;
          let variants, options;
          try {
            variants = JSON.parse(variantsJson.textContent.trim());
            options = JSON.parse(optionsJson.textContent.trim());
          } catch (_e) { return; }

          const modal = document.getElementById('VariantWizardModal');
          if (!modal) return;
          const stepsContainer = modal.querySelector('.variant-wizard-modal__steps');
          const btnPrev = modal.querySelector('.variant-wizard-modal__prev-button');
          const btnNext = modal.querySelector('.variant-wizard-modal__next-button');
          const btnClose = modal.querySelector('.variant-wizard-modal__close-button');

          let current = 0;
          const selections = new Array(options.length);

          function render() {
            stepsContainer.innerHTML = '';
            const opt = options[current];
            const wrapper = document.createElement('div');
            wrapper.className = 'variant-wizard-step';
            const title = document.createElement('h3');
            title.textContent = opt.name;
            wrapper.appendChild(title);
            const list = document.createElement('div');
            list.className = 'variant-wizard-options';
            opt.values.forEach((v) => {
              const btn = document.createElement('button');
              btn.type = 'button';
              btn.className = 'button button--secondary';
              btn.textContent = v;
              if (selections[current] === v) btn.classList.add('is-selected');
              btn.addEventListener('click', () => {
                selections[current] = v;
                btnNext.disabled = false;
                render();
              });
              list.appendChild(btn);
            });
            wrapper.appendChild(list);
            stepsContainer.appendChild(wrapper);
            btnPrev.disabled = current === 0;
            btnNext.textContent = current < options.length - 1 ? 'Avanti' : (window.variantStrings?.addToCart || 'Add to cart');
            btnNext.disabled = !selections[current];
          }

          function open() {
            modal.hidden = false;
            document.body.style.overflow = 'hidden';
            current = 0;
            selections.fill(undefined);
            render();
          }

          function close() {
            modal.hidden = true;
            document.body.style.overflow = '';
          }

          btnPrev.onclick = () => { if (current > 0) { current -= 1; render(); } };
          btnNext.onclick = () => {
            // Selezione implicita: Shopify ha sempre una variante predefinita
            // Se l'utente non cambia nulla, accettiamo quella predefinita
            if (!selections[0] && options.length && variants.length) {
              const defaultVariant = variants.find(v => v.available) || variants[0];
              selections.splice(0, options.length, ...defaultVariant.options);
            }

            if (current < options.length - 1) { current += 1; render(); return; }
            const match = variants.find((vr) => vr.options.every((val, idx) => val === selections[idx]));
            if (!match) return;
            fetch(window.routes.cart_add_url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
              body: new URLSearchParams({ id: String(match.id), quantity: '1' }).toString()
            }).then(() => {
              close();
              document.dispatchEvent(new CustomEvent('cart:updated'));
            }).catch(() => close());
          };
          btnClose.onclick = close;
          modal.querySelector('.variant-wizard-modal__overlay').onclick = close;

          open();
        });
      });
    })();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose classes
  BeezFy.ProductForm = ProductForm;
  BeezFy.VariantSelects = VariantSelects;
  BeezFy.CartNotification = CartNotification;
  BeezFy.StickyHeader = StickyHeader;

})();
